// Inspector — right-pane metadata view for the currently-selected asset.
// Modeled after PlayCanvas's inspector: read-only metadata + a few editable
// fields. Per-kind sections render below the common section.
//
// Empty state shows a hint when no asset is selected.

import React, { useState, useEffect, useRef } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { previewAnimationAsset, stopPreview } from '../../lib/previewAnimationAsset'
import { ogsFileRole } from '../../lib/ogsContentSelect'
import { downloadAssetFile } from '../../lib/assetFileDownload'
import { assetDownloadTarget, downloadProgressText, downloadUnavailableReason } from '../../lib/assetDownloadRules'
import { dialog } from '../../lib/dialog'
import AudioWaveform from './AudioWaveform'
import AssetIcon from './AssetIcon'
import MaterialInspector from './MaterialInspector'
import RigInspector from './RigInspector'
import EnvironmentInspector from './EnvironmentInspector'
import GarmentInspector from './GarmentInspector'
import BlueprintAssetSection from './BlueprintAssetSection'
import { assetKindColor } from '../../lib/assetKindColors'
import { getTextureResidency } from '../../lib/avatarMaterial'
import { readGeneratedMotionExtras } from '../../lib/readGeneratedMotionExtras'
import { renameAsset } from '../../lib/renameAsset'
import { TIER_ORDER, getTextureOverride, setTextureOverride, subscribe as subscribeTextureQuality } from '../../lib/textureQuality'
import TagEditor from './TagEditor'

// Panel chrome, in palette tokens. This used to be a private hex palette —
// a near-black panel with a blue accent — which meant the inspector kept its
// own look no matter which palette was active: a blue Preview button and blue
// selection sitting inside an orange-accented app (Andres, 2026-08-07: "the
// colors on details panel right are mixed"). Chrome follows the theme; only
// the asset-KIND identity colors stay fixed, and for the reason stated in
// assetKindColors.js.
const C = {
  bg:       'var(--m-panel)',
  bgRow:    'var(--m-panel-hi)',
  border:   'var(--m-line)',
  text:     'var(--m-fg)',
  dim:      'var(--m-fg-2)',
  primary:  'var(--m-accent)',
  accent:   'var(--m-accent)',
  ok:       'var(--m-online)',
}

const KIND_LABEL = {
  body_anim:   'BODY ANIMATION',
  face_anim:   'FACE ANIMATION',
  audio:       'AUDIO',
  prefab:      'PREFAB',
  rig:         'RIG',
  texture:     'TEXTURE',
  material:    'MATERIAL',
  environment: 'ENVIRONMENT',
  ogs:         'OGS FILE',
  blueprint_asset: 'BLUEPRINT',
}

function fmtMs(ms) {
  if (ms == null) return '—'
  return `${(ms / 1000).toFixed(2)} s`
}

function fmtBytes(n) {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1048576).toFixed(1)} MB`
}

function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleString()
}

export default function Inspector({ asset, onClose, onNavigateToAsset, onOpenGraph, onModifyGenerated, width }) {
  if (!asset) {
    return (
      <aside className="aos-thin-scroll" style={shellStyle(width, true)}>
        <div style={{ color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11, textAlign: 'center', padding: 20 }}>
          Select an asset to inspect.
        </div>
      </aside>
    )
  }

  return (
    <aside data-testid="asset-inspector" className="aos-thin-scroll" style={shellStyle(width, false)}>
      <Header asset={asset} onClose={onClose} />
      <ThumbBlock asset={asset} />
      {(asset.kind === 'body_anim' || asset.kind === 'face_anim') && <AnimPreviewBar asset={asset} />}
      <CommonSection asset={asset} />
      {/* ENG-1132. In the SHARED chrome, not inside KindSection: every asset
          kind gets tags from one place, so a kind added later cannot quietly
          ship without them — the folder_path failure, which was nine
          hand-maintained copies. */}
      <TagEditor asset={asset} C={C} Section={Section} />
      <KindSection asset={asset} onNavigateToAsset={onNavigateToAsset} onOpenGraph={onOpenGraph} onModifyGenerated={onModifyGenerated} />
    </aside>
  )
}

// ─── Header ─────────────────────────────────────────────────────────────────

function Header({ asset, onClose }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
      background: 'var(--m-bg)',
    }}>
      <span style={{
        background: assetKindColor(asset.kind) + '22',
        color: assetKindColor(asset.kind),
        border: `1px solid ${assetKindColor(asset.kind)}55`,
        fontFamily: 'var(--m-font)', fontSize: 9, fontWeight: 700,
        letterSpacing: '.12em',
        padding: '2px 6px', borderRadius: 3,
      }}>{KIND_LABEL[asset.kind] ?? asset.kind}</span>
      <div style={{ flex: 1 }} />
      {onClose && (
        <button onClick={onClose} style={closeBtn} title="Close inspector">✕</button>
      )}
    </div>
  )
}

// ─── Animation preview controls ─────────────────────────────────────────────
// Plays the clip on the live avatar so the inspector preview (a snapshot/stream
// of the main viewport) shows it. Additive clips go through GestureChannel so
// the delta is layered over the current base pose — i.e. you see the
// reconstructed gesture, not the raw A-pose (Unreal's asset-preview behavior).
// Non-additive clips play exclusively via __DIRECT_PLAY__. Face clips play
// through FaceClipPlayer.manualPlay.
//
// FACE clips get this bar too. previewAnimationAsset has handled face_anim
// since it was written and the right-click ▸ Test menu already routed to it,
// but the bar was gated on body_anim — so from the inspector a face clip had
// no way to play at all, and the snapshot box above it (a mirror of the live
// viewport, not a player) was the only thing that looked like one.
// `additive` is a BODY concept — a delta over a base pose — so the layered
// label and note stay on the body side.

function AnimPreviewBar({ asset }) {
  const isFace = asset.kind === 'face_anim'
  const additive = !isFace && ((asset.additiveType && asset.additiveType !== 'none') || !!asset.additive)

  // Preview goes through the shared previewAnimationAsset helper (the same path
  // the content-browser right-click → Test uses) so key resolution + additive
  // base-pose reconstruction live in one place.
  const play = () =>
    previewAnimationAsset(asset, { animationAssets: CustomAssetManager.listAnimationAssets() })

  const stop = () => stopPreview()

  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: '10px 14px' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={play} style={previewBtn} title="Play this clip on the avatar" data-testid="anim-preview-play">
          ▶ Preview{additive ? ' (layered)' : ''}
        </button>
        <button onClick={stop} style={previewBtnGhost} title="Stop preview" data-testid="anim-preview-stop">■ Stop</button>
      </div>
      {additive && (
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 6, lineHeight: 1.4 }}>
          Additive — plays layered on the current base pose, so you see the full
          gesture (not the raw delta).
        </div>
      )}
    </div>
  )
}

// ─── Thumbnail / preview block ──────────────────────────────────────────────

function ThumbBlock({ asset }) {
  return (
    <div style={{
      padding: 14, borderBottom: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    }}>
      <AssetIcon asset={asset} size={220} />
      {asset.kind === 'audio' && asset.url && (
        <audio src={asset.url} controls style={{ width: '100%' }} />
      )}
    </div>
  )
}

// ─── Common section: id / name / path / created ────────────────────────────

function CommonSection({ asset }) {
  return (
    <Section title="Asset" defaultOpen={false}>
      <Field label="ID">
        <ReadVal value={asset.rawId} mono small />
      </Field>
      {asset.kind === 'ogs'
        ? <Field label="Name"><ReadVal value={asset.name} mono /></Field>
        : <EditableName asset={asset} />}
      <Field label="Path">
        <ReadVal value={'/' + (asset.path?.join('/') ?? '')} mono />
      </Field>
      <Field label="Created">
        <ReadVal value={fmtDate(asset.raw?.created_at)} mono small />
      </Field>
    </Section>
  )
}

function EditableName({ asset }) {
  const [name, setName] = useState(asset.name)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => { setName(asset.name); setError(null) }, [asset.id])

  const commit = async () => {
    if (name === asset.name) return
    setSaving(true); setError(null)
    try {
      // Shared per-kind rename (also used by the content-browser inline rename).
      // Throws for kinds that can't be renamed (rig — would change the slug).
      await renameAsset(asset, name)
    } catch (e) {
      setError(String(e.message ?? e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Field label="Name">
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        style={editableInput}
      />
      {error && <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: 'var(--m-danger)', marginTop: 4 }}>{error}</div>}
      {saving && <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: C.dim, marginTop: 4 }}>Saving…</div>}
    </Field>
  )
}

// ─── Per-kind sections ─────────────────────────────────────────────────────

function KindSection({ asset, onNavigateToAsset, onOpenGraph, onModifyGenerated }) {
  if (asset.kind === 'body_anim') return (
    <>
      <AnimSection asset={asset} />
      <GenerationSection asset={asset} onModifyGenerated={onModifyGenerated} />
      <AdditiveSettings asset={asset} />
    </>
  )
  if (asset.kind === 'face_anim') return <AnimSection asset={asset} />
  if (asset.kind === 'audio')    return <AudioSection asset={asset} />
  if (asset.kind === 'prefab')   return <PrefabSection asset={asset} />
  if (asset.kind === 'material') return <MaterialInspector asset={asset} onNavigateToAsset={onNavigateToAsset} onOpenGraph={onOpenGraph} />
  if (asset.kind === 'texture')  return <TextureSection asset={asset} />
  if (asset.kind === 'rig')      return <RigInspector asset={asset} onNavigateToAsset={onNavigateToAsset} />
  if (asset.kind === 'environment') return <EnvironmentInspector asset={asset} />
  if (asset.kind === 'garment')     return <GarmentInspector asset={asset} />
  if (asset.kind === 'ogs')      return <OgsSection asset={asset} />
  if (asset.kind === 'blueprint_asset')
    return <BlueprintAssetSection asset={asset} Section={Section} Field={Field} ReadVal={ReadVal} />
  return null
}

function AnimSection({ asset }) {
  return (
    <Section title={asset.kind === 'body_anim' ? 'Body animation' : 'Face animation'} defaultOpen={false}>
      <Field label="Duration"><ReadVal value={fmtMs(asset.durationMs)} mono /></Field>
      {asset.kind === 'body_anim' && (
        <>
          <Field label="Category"><ReadVal value={asset.category ?? '—'} mono /></Field>
          <LoopableField asset={asset} />
        </>
      )}
      <Field label="Runtime clip name"><ReadVal value={`${asset.kind === 'body_anim' ? 'custom_body_' : 'custom_face_'}${asset.rawId}`} mono small /></Field>
      {asset.url && <SourceLink url={asset.url} />}
    </Section>
  )
}

// A body clip minted by the Generate panel carries its provenance in the GLB's
// glTF scene extras (aosGeneratedMotion). A marker → "GENERATED" badge + params
// + Modify; no marker → renders nothing; a fetch/parse FAILURE → inline error,
// never a silently-hidden section.

// Session memo of the provenance verdict, keyed url+version (same token the
// animCache uses), so re-selecting a clip whose verdict is known skips the
// multi-MB GLB re-download. Only success verdicts are cached — errors may be
// transient, so they re-fetch.
const _provenanceCache = new Map() // `${url}|${version}` -> (extras | null)

function GenerationSection({ asset, onModifyGenerated }) {
  const [state, setState] = useState({ phase: 'loading' }) // loading | none | error | ok

  useEffect(() => {
    let cancelled = false
    setState({ phase: 'loading' })
    if (!asset.url) { setState({ phase: 'none' }); return }
    // Version token = the boot loader's animCache key (AvatarModel keys body GLBs
    // by updatedAt, falling back to the asset id) so this hits the warm entry.
    const version = asset.raw?.updatedAt ?? asset.raw?.updated_at ?? asset.rawId ?? null
    const cacheKey = `${asset.url}|${version}`
    if (_provenanceCache.has(cacheKey)) {
      const extras = _provenanceCache.get(cacheKey)
      setState(extras ? { phase: 'ok', extras } : { phase: 'none' })
      return
    }
    readGeneratedMotionExtras(asset.url, version)
      .then((extras) => {
        _provenanceCache.set(cacheKey, extras ?? null)
        if (!cancelled) setState(extras ? { phase: 'ok', extras } : { phase: 'none' })
      })
      .catch((e) => { if (!cancelled) setState({ phase: 'error', error: String(e.message ?? e) }) })
    return () => { cancelled = true }
    // Include the version-token sources (they build the cacheKey the effect reads)
    // so a same-id/same-url re-upload — which bumps updatedAt / rawId — re-reads
    // provenance instead of serving a stale phase from the old key.
  }, [asset.id, asset.url, asset.raw?.updatedAt, asset.raw?.updated_at, asset.rawId])

  if (state.phase === 'loading' || state.phase === 'none') return null

  if (state.phase === 'error') {
    return (
      <Section title="Generation">
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-danger)', lineHeight: 1.5 }}>
          Couldn't read generation metadata: {state.error}
        </div>
      </Section>
    )
  }

  const g = state.extras
  const modify = () => {
    // Prefill the docked Generate panel from the GLB provenance + the asset row.
    onModifyGenerated?.(
      {
        prompt: g.prompt ?? '',
        seed: g.seed,
        duration_sec: g.duration_sec,
        diffusion_steps: g.diffusion_steps,
        keepRootMotion: !!g.keepRootMotion,
        waypoints: g.waypoints ?? null,
        path_mode: g.path_mode ?? null,
        walk_speed: g.walk_speed ?? null,
        timing_mode: g.timing_mode ?? null,
        constraint_stride: g.constraint_stride ?? null,
        constraint_weight: g.constraint_weight ?? null,
        smooth: g.smooth ?? null,
        corner_radius_m: g.corner_radius_m ?? null,
        name: asset.name,
        category: asset.category ?? 'other',
        loopable: !!asset.loopable,
        additive: !!asset.additive,
      },
      { id: asset.rawId, name: asset.name, path: asset.path ?? [] },
    )
  }

  return (
    <Section title="Generation">
      <div style={{ marginBottom: 10 }}>
        <span style={generatedBadge}>⚡ GENERATED</span>
      </div>
      <Field label="Prompt"><ReadVal value={g.prompt ?? '—'} multi /></Field>
      <Field label="Seed"><ReadVal value={g.seed != null ? String(g.seed) : '—'} mono /></Field>
      <Field label="Duration"><ReadVal value={g.duration_sec != null ? `${g.duration_sec} s` : '—'} mono /></Field>
      <Field label="Diffusion steps"><ReadVal value={g.diffusion_steps != null ? String(g.diffusion_steps) : '—'} mono /></Field>
      {/* The stored flag is `keepRootMotion`; the wizard's control is its inverse, and the
          two must read the same way or a round-trip looks like it flipped. */}
      <Field label="In place"><ReadVal value={g.keepRootMotion ? 'no' : 'yes'} mono /></Field>
      {g.waypoints?.length > 0 && <Field label="Waypoints"><ReadVal value={`${g.waypoints.length} points (${g.path_mode ?? 'navmesh'})`} mono /></Field>}
      {g.waypoints?.length > 0 && (g.constraint_stride != null || g.constraint_weight != null) && (
        <Field label="Path tracking">
          <ReadVal value={`stride ${g.constraint_stride ?? '—'} · weight ${g.constraint_weight ?? '—'}${g.smooth ? ` · ${g.smooth}` : ''}${g.smooth === 'curve' && g.corner_radius_m != null ? ` (${g.corner_radius_m} m radius)` : ''}`} mono />
        </Field>
      )}
      <Field label="Generated"><ReadVal value={fmtDate(g.generatedAt ? new Date(g.generatedAt).toISOString() : null)} mono small /></Field>
      <div style={{ display: 'flex', marginTop: 4 }}>
        <button onClick={modify} style={previewBtn} title="Re-open the Generate panel prefilled from this clip's parameters">
          ✎ Modify
        </button>
      </div>
    </Section>
  )
}

// Editable "loopable" flag (ENG-524). Mirrors Unreal's asset-level bLoop: this
// is the clip's default looping behavior, used to seed a Play Clip node's Loop
// when the clip is selected. Persisted on the animation asset.
function LoopableField({ asset }) {
  const [loopable, setLoopable] = useState(!!asset.loopable)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => { setLoopable(!!asset.loopable); setError(null) }, [asset.id])

  const toggle = async (v) => {
    setLoopable(v); setSaving(true); setError(null)
    try {
      await CustomAssetManager.updateAnimationAsset(asset.rawId, { loopable: v })
    } catch (e) {
      setError(String(e.message ?? e)); setLoopable(!v)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Field label="Loopable">
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input
          type="checkbox" checked={loopable}
          onChange={e => toggle(e.target.checked)}
          style={{ accentColor: C.accent, cursor: 'pointer' }}
        />
        <span style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: C.text }}>{loopable ? 'yes' : 'no'}</span>
        {saving && <span style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: C.dim }}>Saving…</span>}
      </label>
      <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 3, lineHeight: 1.4 }}>
        Default loop for this clip — a Play Clip node seeds its Loop from this when the clip is selected.
      </div>
      {error && <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: 'var(--m-danger)', marginTop: 4 }}>{error}</div>}
    </Field>
  )
}

// ─── ENG-442: Unreal-parity Additive Settings ───────────────────────────────
//
// Mirrors Unreal's "Additive Settings" panel. Artists set the same controls
// here that they would on the source Unreal asset; the runtime bake
// (src/lib/additiveBake.js) reproduces the corresponding math at GLB load.

const ADDITIVE_TYPE_OPTIONS = [
  { value: 'none',  label: 'None' },
  { value: 'local', label: 'Local Space' },
  { value: 'mesh',  label: 'Mesh Space' },
]

const BASE_POSE_TYPE_OPTIONS = [
  { value: 'none',        label: 'None' },
  { value: 'ref_pose',    label: 'Skeleton Reference Pose' },
  { value: 'anim_scaled', label: 'Selected animation scaled' },
  { value: 'anim_frame',  label: 'Selected animation frame' },
  { value: 'local_frame', label: 'Frame from this animation' },
]

function AdditiveSettings({ asset }) {
  const [additiveType, setAdditiveType] = useState(asset.additiveType ?? 'none')
  const [basePoseType, setBasePoseType] = useState(asset.basePoseType ?? 'none')
  const [basePoseAssetId, setBasePoseAssetId] = useState(asset.basePoseAssetId ?? '')
  const [refFrameIndex, setRefFrameIndex] = useState(asset.refFrameIndex ?? 0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setAdditiveType(asset.additiveType ?? 'none')
    setBasePoseType(asset.basePoseType ?? 'none')
    setBasePoseAssetId(asset.basePoseAssetId ?? '')
    setRefFrameIndex(asset.refFrameIndex ?? 0)
    setError(null)
  }, [asset.id])

  const save = async (patch) => {
    setSaving(true); setError(null)
    try {
      await CustomAssetManager.updateAnimationAsset(asset.rawId, patch)
    } catch (e) {
      setError(String(e.message ?? e))
    } finally {
      setSaving(false)
    }
  }

  const onAdditiveType = (v) => {
    setAdditiveType(v)
    const patch = { additiveType: v }
    // Additive needs a base pose; default to "Frame from this animation" so the
    // clip is immediately valid (matches the historical frame-0 behavior).
    if (v !== 'none' && (basePoseType === 'none')) {
      setBasePoseType('local_frame')
      patch.basePoseType = 'local_frame'
    }
    save(patch)
  }

  const onBasePoseType = (v) => {
    setBasePoseType(v)
    save({ basePoseType: v })
  }

  const onBasePoseAsset = (v) => {
    setBasePoseAssetId(v)
    save({ basePoseAssetId: v || null })
  }

  const onRefFrame = (v) => {
    const n = Number.isFinite(+v) ? Math.max(0, Math.floor(+v)) : 0
    setRefFrameIndex(n)
    save({ refFrameIndex: n })
  }

  const showBasePose = additiveType !== 'none'
  const needsBaseClip = basePoseType === 'anim_frame' || basePoseType === 'anim_scaled'
  const needsRefFrame = basePoseType === 'local_frame' || basePoseType === 'anim_frame'

  // Body anims usable as a base pose (exclude self).
  const bodyAnims = CustomAssetManager.listAnimationAssets()
    .filter(a => a.kind === 'body' && a.id !== asset.rawId)

  return (
    <Section title="Additive Settings">
      <Field label="Additive Anim Type">
        <EnumSelect value={additiveType} options={ADDITIVE_TYPE_OPTIONS} onChange={onAdditiveType} />
      </Field>
      {showBasePose && (
        <Field label="Base Pose Type">
          <EnumSelect value={basePoseType} options={BASE_POSE_TYPE_OPTIONS} onChange={onBasePoseType} />
        </Field>
      )}
      {showBasePose && needsBaseClip && (
        <Field label="Base Pose Animation">
          <select value={basePoseAssetId ?? ''} onChange={e => onBasePoseAsset(e.target.value)} style={selectStyle}>
            <option value="">— select a clip —</option>
            {bodyAnims.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {!basePoseAssetId && (
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: 'var(--m-warn)', marginTop: 4 }}>
              Pick a base pose clip — required for this Base Pose Type.
            </div>
          )}
        </Field>
      )}
      {showBasePose && needsRefFrame && (
        <Field label="Ref Frame Index">
          <input
            type="number" min={0} step={1}
            value={refFrameIndex}
            onChange={e => setRefFrameIndex(e.target.value)}
            onBlur={e => onRefFrame(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
            style={editableInput}
          />
        </Field>
      )}
      {mesh_hint(additiveType)}
      {error && <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: 'var(--m-danger)', marginTop: 4 }}>{error}</div>}
      {saving && <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: C.dim, marginTop: 4 }}>Saving…</div>}
    </Section>
  )
}

function mesh_hint(additiveType) {
  if (additiveType !== 'mesh') return null
  return (
    <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: C.dim, marginTop: 2, lineHeight: 1.4 }}>
      Mesh Space: rotation deltas are computed in component space (aim/lean
      offsets). Reload the avatar to re-bake after changing this.
    </div>
  )
}

function EnumSelect({ value, options, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function AudioSection({ asset }) {
  return (
    <Section title="Audio">
      <Field label="Duration"><ReadVal value={fmtMs(asset.durationMs)} mono /></Field>
      <Field label="Transcript">
        <ReadVal value={asset.transcript || '—'} multi />
      </Field>
      {asset.url && <SourceLink url={asset.url} />}
    </Section>
  )
}

function PrefabSection({ asset }) {
  const p = asset.raw ?? {}
  // The asset-manager returns id fields in camelCase; only some carry
  // snake_case aliases. Read both so saved values don't render as "—".
  const bodyAnimId = p.body_animation_id ?? p.bodyAnimationId ?? null
  const faceAnimId = p.face_animation_id ?? p.faceAnimationId ?? null
  const audioId    = p.audio_asset_id ?? p.audioAssetId ?? null
  const bodyClip   = p.body_clip_name ?? p.bodyClipName ?? null
  const faceClip   = p.face_clip_name ?? p.faceClipName ?? null
  const blendIn    = p.blend_in_ms ?? p.blendInMs ?? 180
  const blendOut   = p.blend_out_ms ?? p.blendOutMs ?? 220
  const durationMs = p.duration_ms ?? p.durationMs ?? null

  const nameFor = (list, id) => id ? (list.find(a => a.id === id)?.name ?? `#${String(id).slice(0, 8)}`) : null
  const bodyLabel  = bodyAnimId ? nameFor(CustomAssetManager.listAnimationAssets(), bodyAnimId) : (bodyClip ?? '—')
  const faceLabel  = faceAnimId ? nameFor(CustomAssetManager.listAnimationAssets(), faceAnimId) : (faceClip ?? '—')
  const audioLabel = audioId ? nameFor(CustomAssetManager.listAudioAssets(), audioId) : '—'

  // Same playback path as the editor's Test button and the right-click Test.
  const test = () => { try { window.__EVENT_RUNTIME__?.playPrefabByTag?.(p.tag) } catch { /* ignore */ } }

  return (
    <Section title="Prefab">
      {p.tag && (
        <div style={{ display: 'flex', marginBottom: 10 }}>
          <button onClick={test} style={previewBtn} title="Play this prefab on the avatar">▶ Test</button>
        </div>
      )}
      <Field label="Tag"><ReadVal value={p.tag} mono /></Field>
      <Field label="Status">
        <span style={{ color: p.status === 'ready' ? C.ok : C.dim, fontFamily: 'var(--m-font)', fontSize: 11 }}>
          {p.status ?? 'draft'}
        </span>
      </Field>
      <Field label="Duration"><ReadVal value={fmtMs(durationMs)} mono /></Field>
      <Field label="Blend in / out"><ReadVal value={`${blendIn} / ${blendOut} ms`} mono /></Field>
      <Field label="Loop"><ReadVal value={p.loop ? 'yes' : 'no'} mono /></Field>
      <Field label="Body anim"><ReadVal value={bodyLabel} mono small /></Field>
      <Field label="Face anim"><ReadVal value={faceLabel} mono small /></Field>
      <Field label="Audio"><ReadVal value={audioLabel} mono small /></Field>
    </Section>
  )
}

function TextureSection({ asset }) {
  // Legacy slot textures (rawId is "role.map") don't carry width/height/etc.
  const isLegacy = typeof asset.rawId === 'string' && asset.rawId.includes('.')
  return (
    <Section title="Texture">
      {!isLegacy && (
        <>
          <Field label="Dimensions"><ReadVal value={asset.width && asset.height ? `${asset.width} × ${asset.height}` : '—'} mono /></Field>
          <Field label="Format"><ReadVal value={asset.format ?? '—'} mono /></Field>
          <Field label="Size"><ReadVal value={asset.byteSize ? `${(asset.byteSize / 1024).toFixed(1)} KB` : '—'} mono /></Field>
        </>
      )}
      {isLegacy && (
        <Field label="Slot"><ReadVal value={asset.rawId} mono /></Field>
      )}
      {asset.url && <SourceLink url={asset.url} />}
      {!isLegacy && <TextureResolutions asset={asset} />}
    </Section>
  )
}

// ── Texture quality tiers (ENG-757) ─────────────────────────────────────────
// Per-rung availability of the server-side KTX2 size ladder, what this
// session has downloaded / is displaying, and a LOCAL per-texture quality pin
// (this browser only — co-editors on slower machines pick their own).

function TextureResolutions({ asset }) {
  const [, setTick] = useState(0)
  useEffect(() => subscribeTextureQuality(() => setTick(t => t + 1)), [])

  const row = asset.raw ?? {}
  const residency = getTextureResidency(row)
  const override = getTextureOverride(asset.rawId)

  const stateOf = (r) => {
    if (residency.boundEdge === r.edge) return { label: 'displayed', color: C.ok }
    if (r.downloaded) return { label: 'downloaded', color: C.primary }
    if (r.status === 'done') return { label: 'remote', color: C.dim }
    if (r.status === 'pending') return { label: 'transcoding…', color: C.dim }
    if (r.status === 'error') return { label: 'error', color: 'var(--m-danger)' }
    return { label: r.status || 'remote', color: C.dim }
  }
  const edgeLabel = (edge) =>
    edge === Infinity
      ? (asset.width && asset.height ? `${asset.width} × ${asset.height} (full)` : 'full res')
      : `${edge} px`

  return (
    <>
      <Field label="Resolutions">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {residency.rungs.map((r) => {
            const s = stateOf(r)
            return (
              <div key={String(r.edge)} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: 'var(--m-font)', fontSize: 11 }}>
                <span style={{ color: C.text }}>{edgeLabel(r.edge)}</span>
                <span style={{ color: C.dim }}>{r.byteSize ? fmtBytes(r.byteSize) : ''}</span>
                <span style={{ color: s.color }}>{s.label}</span>
              </div>
            )
          })}
          {residency.rungs.length === 1 && (
            <span style={{ color: C.dim, fontSize: 10 }}>
              no size ladder yet — run the texture backfill to generate 512/1024/2048 variants
            </span>
          )}
        </div>
      </Field>
      <Field label="Quality">
        <select
          value={override ?? ''}
          onChange={(e) => setTextureOverride(asset.rawId, e.target.value || null)}
          style={{
            background: C.bgRow, color: C.text, border: `1px solid ${C.border}`,
            borderRadius: 3, fontFamily: 'var(--m-font)', fontSize: 11, padding: '2px 6px', width: '100%',
          }}
          data-testid="texture-quality-override"
        >
          <option value="">Follow global setting</option>
          {TIER_ORDER.map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)} (this device only)</option>
          ))}
        </select>
      </Field>
    </>
  )
}

// ─── OGS bundle file ────────────────────────────────────────────────────────
// The /ogs collection is keyed by bare filename — "Replace" uploads the picked
// file under the selected name (renaming it if it differs), which swaps the row.

const OGS_ROLE_HINT = {
  required: 'Core render-bundle file — the OGS pipeline can\'t render without it.',
  optional: 'Optional — enables the live-pose path when present.',
  extra:    'Not part of the render bundle — the runtime ignores it.',
}

function OgsSection({ asset }) {
  const inputRef = useRef(null)
  const [status, setStatus] = useState(null) // { phase: 'uploading'|'downloading'|'ok'|'error', text }
  useEffect(() => { setStatus(null) }, [asset.id])

  const role = ogsFileRole(asset.rawId)
  const ext = asset.rawId.includes('.') ? '.' + asset.rawId.split('.').pop() : undefined

  const replaceWith = async (file) => {
    if (!file) return
    // Upload under the existing filename so the server replaces this row.
    const upload = file.name === asset.rawId ? file : new File([file], asset.rawId, { type: file.type })
    setStatus({ phase: 'uploading', text: 'Uploading… 0%' })
    try {
      await CustomAssetManager.uploadOgsAsset(upload, {
        onProgress: p => setStatus({
          phase: 'uploading',
          text: p.fraction >= 0 ? `Uploading… ${Math.round(p.fraction * 100)}%` : 'Uploading…',
        }),
      })
      setStatus({ phase: 'ok', text: `Replaced ${asset.rawId} — reload the character to pick it up.` })
    } catch (e) {
      setStatus({ phase: 'error', text: `Replace failed: ${e?.message ?? e}` })
    }
  }

  // Download this exact file. It is fetched through the asset-manager base with
  // the app's credentials (assetFileDownload) — the row's own fileUrl is an
  // AAM-relative path, so a page-relative GET of it answers a phantom 404 from
  // convorcher instead.
  const target = assetDownloadTarget(asset)
  const download = async () => {
    setStatus({ phase: 'downloading', text: downloadProgressText(0, null) })
    try {
      const { filename } = await downloadAssetFile(asset, {
        onProgress: ({ received, total }) => setStatus({
          phase: 'downloading', text: downloadProgressText(received, total),
        }),
      })
      setStatus({ phase: 'ok', text: `Downloaded ${filename}` })
    } catch (e) {
      setStatus({ phase: 'error', text: `Download failed: ${e?.message ?? e}` })
    }
  }

  const del = async () => {
    const ok = await dialog.confirm({
      title: 'Delete OGS file?',
      body: role === 'required'
        ? `"${asset.rawId}" is required by the render bundle — this character won't render as OGS until it's re-uploaded.`
        : `Delete "${asset.rawId}" from the OGS bundle?`,
      confirmLabel: 'Delete',
      danger: true,
    })
    if (!ok) return
    try {
      await CustomAssetManager.deleteOgsAsset(asset.rawId)
    } catch (e) {
      setStatus({ phase: 'error', text: `Delete failed: ${e?.message ?? e}` })
    }
  }

  const busy = status?.phase === 'uploading' || status?.phase === 'downloading'
  return (
    <Section title="OGS file">
      <Field label="Size"><ReadVal value={fmtBytes(asset.byteSize)} mono /></Field>
      <Field label="Role">
        <ReadVal value={role} mono />
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 3, lineHeight: 1.4 }}>
          {OGS_ROLE_HINT[role]}
        </div>
      </Field>
      {asset.url && <SourceLink url={asset.url} />}
      <input ref={inputRef} type="file" accept={ext} style={{ display: 'none' }}
        onChange={e => { replaceWith(e.target.files?.[0]); e.target.value = '' }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {/* Shown even when the row states no file URL: disabled with the reason
            in its tooltip, never hidden. */}
        <button onClick={download} disabled={busy || !target} style={previewBtn}
          data-testid="download-ogs-file"
          title={target ? `Download ${target.filename}` : downloadUnavailableReason(asset)}>
          ⬇ Download
        </button>
        <button onClick={() => inputRef.current?.click()} disabled={busy} style={previewBtn}
          title={`Upload a new ${asset.rawId} in place of the current file`}>
          ⇪ Replace file…
        </button>
        <button onClick={del} disabled={busy} style={dangerBtn} title="Delete this file from the bundle">
          🗑 Delete
        </button>
      </div>
      {status && (
        <div style={{
          fontFamily: 'var(--m-font)', fontSize: 9.5, marginTop: 6, lineHeight: 1.4,
          color: status.phase === 'error' ? 'var(--m-danger)' : status.phase === 'ok' ? C.ok : C.dim,
        }}>{status.text}</div>
      )}
    </Section>
  )
}

// ─── Primitives ─────────────────────────────────────────────────────────────

// Collapsible section (Unreal-style). Open/closed state persists per title in
// localStorage so a user's collapse choices stick across asset selections.
// `defaultOpen` only sets the initial state before the user has toggled it.
// Exported so TagEditor renders in the SAME chrome as every other block
// (collapse, divider, header) instead of a hand-rolled label beside them.
export function Section({ title, children, defaultOpen = true }) {
  const storageKey = `aos-inspector-sec:${title}`
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey)
    return saved == null ? defaultOpen : saved === '1'
  })
  const toggle = () => {
    setOpen(o => {
      const next = !o
      try { localStorage.setItem(storageKey, next ? '1' : '0') } catch { /* private mode */ }
      return next
    })
  }
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button onClick={toggle} style={sectionHeaderBtn} title={open ? 'Collapse' : 'Expand'}>
        <span style={{
          display: 'inline-block', width: 10, color: C.dim,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .1s',
        }}>▸</span>
        <span>{title}</span>
      </button>
      {open && <div style={{ padding: '0 14px 10px 28px' }}>{children}</div>}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.1em',
        textTransform: 'uppercase', color: C.dim, marginBottom: 3,
      }}>{label}</div>
      {children}
    </div>
  )
}

function ReadVal({ value, mono, small, multi }) {
  return (
    <div style={{
      color: C.text,
      fontFamily: mono ? 'var(--m-mono)' : 'inherit',
      fontSize: small ? 10 : 11,
      whiteSpace: multi ? 'pre-wrap' : 'nowrap',
      overflow: multi ? 'visible' : 'hidden',
      textOverflow: 'ellipsis',
    }} title={typeof value === 'string' ? value : undefined}>
      {value ?? '—'}
    </div>
  )
}

function SourceLink({ url }) {
  return (
    <Field label="Source URL">
      <a href={url} target="_blank" rel="noreferrer" style={{
        color: C.primary, fontFamily: 'var(--m-font)', fontSize: 10,
        wordBreak: 'break-all',
      }}>{url}</a>
    </Field>
  )
}

function shellStyle(width, empty) {
  return {
    width, flexShrink: 0,
    background: C.bg,
    overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
    ...(empty ? { justifyContent: 'center' } : null),
  }
}

const editableInput = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--m-bg)', border: `1px solid ${C.border}`,
  borderRadius: 3, padding: '4px 8px',
  color: C.text, fontSize: 11, fontFamily: 'var(--m-font)',
  outline: 'none',
}

const selectStyle = {
  ...editableInput,
  cursor: 'pointer',
}

// "GENERATED" provenance chip — same shape as the Header kind badge, tinted
// with the primary accent so it reads as a first-class marker.
const generatedBadge = {
  background: C.primary + '22',
  color: C.primary,
  border: `1px solid ${C.primary}55`,
  fontFamily: 'var(--m-font)', fontSize: 9, fontWeight: 700,
  letterSpacing: '.12em',
  padding: '2px 6px', borderRadius: 3,
}

const previewBtn = {
  flex: 1,
  background: 'color-mix(in srgb, var(--m-accent) 22%, transparent)',
  border: '1px solid var(--m-accent)',
  color: 'var(--m-fg)', fontFamily: 'var(--m-font)', fontSize: 11,
  padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
}

const dangerBtn = {
  background: 'transparent', border: '1px solid color-mix(in srgb, var(--m-danger) 40%, transparent)',
  color: 'var(--m-danger)', fontFamily: 'var(--m-font)', fontSize: 11,
  padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
}

const previewBtnGhost = {
  background: 'transparent', border: `1px solid ${C.border}`,
  color: C.dim, fontFamily: 'var(--m-font)', fontSize: 11,
  padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
}

const sectionHeaderBtn = {
  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: '9px 14px', textAlign: 'left',
  fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.12em',
  textTransform: 'uppercase', color: C.dim,
}

const closeBtn = {
  background: 'transparent', border: `1px solid ${C.border}`,
  color: C.dim, fontFamily: 'var(--m-font)', fontSize: 10,
  width: 22, height: 22, borderRadius: 3, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}
